import { DialogContent, Stack } from "@mui/material";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { SelectableBox } from "@src/appV2/redesign/components/SelectableBox";
import { Pill, Text, Title } from "@src/appV2/redesign/system/ui-components";
import { useFormContext } from "react-hook-form";

import { type PostShiftReviewFormValues, type StepAction } from "../types";

export interface ChooseAnonymityStepProps {
  isSubmitting?: boolean;
  onAction: (action: StepAction) => void;
}

export function ChooseAnonymityStep(props: ChooseAnonymityStepProps) {
  const { isSubmitting = false, onAction } = props;
  const { watch, setValue } = useFormContext<PostShiftReviewFormValues>();
  const isAnonymous = watch("isAnonymous");

  const hasSelection = isAnonymous !== undefined;

  return (
    <>
      <DialogContent sx={{ py: 2 }}>
        <Stack spacing={6}>
          <Stack spacing={1}>
            <Title variant="h3" component="h3">
              Post anonymously?
            </Title>
            <Text variant="body2" color="text.secondary">
              You choose. But here&apos;s what we know.
            </Text>
          </Stack>

          <Stack spacing={4}>
            <SelectableBox
              variant="secondary"
              isSelected={isAnonymous === false}
              disabled={isSubmitting}
              startIconType="user"
              sx={{ alignItems: "flex-start", textAlign: "left" }}
              onToggleSelect={() => {
                setValue("isAnonymous", false, { shouldValidate: true });
              }}
            >
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  justifyContent="space-between"
                >
                  <Text bold variant="body1">
                    Post with my name
                  </Text>
                  <Pill
                    size="small"
                    label="Recommended"
                    backgroundColor={(theme) => theme.palette.intent?.success.background}
                    color={(theme) => theme.palette.intent?.success.text}
                  />
                </Stack>
                <Text variant="body2">
                  Named reviews are rated as significantly more trustworthy by peers and get more
                  likes and replies.
                </Text>
              </Stack>
            </SelectableBox>

            <SelectableBox
              variant="secondary"
              isSelected={isAnonymous === true}
              disabled={isSubmitting}
              startIconType="user-remove"
              sx={{ alignItems: "flex-start", textAlign: "left" }}
              onToggleSelect={() => {
                setValue("isAnonymous", true, { shouldValidate: true });
              }}
            >
              <Stack spacing={2}>
                <Text bold variant="body1">
                  Post anonymously
                </Text>
                <Text variant="body2">
                  Your name won&apos;t be shown. Replies and likes still work.
                </Text>
              </Stack>
            </SelectableBox>
          </Stack>

          <Text variant="caption" color="text.secondary">
            Reviews that use inappropriate language or certain references to patients and specific
            workplace personnel will be removed.
          </Text>
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          isLoading={isSubmitting}
          disabled={!hasSelection}
          onClick={() => {
            if (isAnonymous !== undefined) {
              onAction({
                type: "NEXT_STEP",
                data: { isAnonymous },
              });
            }
          }}
        >
          Continue
        </LoadingButton>
      </DialogFooter>
    </>
  );
}
